import { useCallback, useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { usePopper } from "react-popper";

type Props = {
  readonly value: string;
  readonly onChange: (color: string) => void;
};

export const ColorInput = ({ value, onChange }: Props) => {
  const [colorText, setColorText] = useState<string>(value);

  useEffect(() => {
    setColorText(value);
  }, [value]);

  const [colorRef, setColorRef] = useState<HTMLDivElement | null>(null);
  const [colorPickerRef, setColorPickerRef] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(colorRef, colorPickerRef, {
    placement: "bottom-start",
  });

  const [openColorPicker, setOpenColorPicker] = useState(false);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const paths = event.composedPath();
      if (openColorPicker && !paths.some((path: HTMLElement) => ["color-picker", "color-box"].includes(path.id))) {
        setOpenColorPicker(false);
      }
    },
    [openColorPicker]
  );

  useEffect(() => {
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [handleClick]);

  const isValidColorText = useCallback((val: string) => {
    return val.match("#[a-fA-F0-9]{6}");
  }, []);

  return (
    <div className="flex flex-row">
      <div
        id="color-box"
        className="h-[28px] w-[28px] border-y border-l border-[#ccc] cursor-pointer"
        style={{ backgroundColor: value }}
        ref={setColorRef}
        onClick={() => setOpenColorPicker(!openColorPicker)}
      />
      <input
        type="text"
        className="w-[100px] px-[8px] py-[4px] border border-[#ccc]"
        value={colorText}
        maxLength={7}
        onChange={(e) => {
          const v = `${e.target.value.match("^#") ? "" : "#"}${e.target.value}`;
          setColorText(v);
          if (isValidColorText(v)) {
            onChange(v);
          }
        }}
        onBlur={(e) => {
          if (!isValidColorText(e.target.value)) {
            setColorText(value);
          }
        }}
      />
      {openColorPicker && (
        <div
          id="color-picker"
          ref={setColorPickerRef}
          className="z-10"
          style={{ ...styles.popper }}
          {...attributes.popper}
        >
          <SketchPicker
            color={value}
            disableAlpha={true}
            presetColors={[]}
            onChange={(v) => {
              onChange(v.hex);
            }}
          />
        </div>
      )}
    </div>
  );
};
